import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, CheckCircle, AlertTriangle, XCircle, Wrench, Loader2, Scale, MinusCircle } from "lucide-react";
import api from "@/lib/api";

// Use the actual 16 sound types directly from the classifier
// Each has: name, measured_db, ideal_db, status (normal/elevated/critical)
function prepareSoundBars(soundTypes) {
  if (!soundTypes || !soundTypes.length) return [];
  return soundTypes
    .filter(s => s.measured_db > 0 || s.ideal_db > 0)
    .map(s => ({
      name: s.name,
      measured: s.measured_db || 0,
      ideal: s.ideal_db || 0,
      status: s.status || "normal",
    }));
}

function SoundBar({ item, maxVal, index }) {
  const measuredPct = (item.measured / maxVal) * 100;
  const idealPct = (item.ideal / maxVal) * 100;

  // Color is determined by the CLASSIFICATION STATUS, not raw dB comparison.
  // The classification engine considers car type, age, mileage, fuel type —
  // so measured > ideal can still be "normal" for a specific vehicle.
  const notAssessed = item.status === "not_assessed";  // turbo band when turbo fitment is unknown
  const solidColor = item.status === "critical" ? "#EF4444"
    : item.status === "elevated" ? "#FFB347"
    : notAssessed ? "#94A3B8"
    : "#4CD964";  // normal/low = green

  const isNormal = item.status !== "critical" && item.status !== "elevated";
  const measuredExceedsIdeal = item.measured > item.ideal;

  // PDF-safe styles (no repeating-linear-gradient — breaks Safari PDF export)
  const postServiceStyle = {
    backgroundColor: "#E5E7EB",
    border: "1px solid #D1D5DB",
  };

  const idealOverlayStyle = {
    backgroundColor: "rgba(0,0,0,0.12)",
  };

  // Icon matches status, not dB comparison
  const StatusIcon = notAssessed
    ? <MinusCircle size={15} className="text-slate-400 flex-shrink-0" data-testid={`sound-status-icon-${index}`} />
    : isNormal
    ? <CheckCircle size={15} className="text-green-500 flex-shrink-0" data-testid={`sound-status-icon-${index}`} />
    : item.status === "critical"
    ? <XCircle size={15} className="text-red-500 flex-shrink-0" data-testid={`sound-status-icon-${index}`} />
    : <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" data-testid={`sound-status-icon-${index}`} />;

  return (
    <div
      className="flex items-center gap-3 py-3"
      data-testid={`sound-row-${index}`}
      data-sound-name={item.name}
      data-status={item.status}
      data-measured={item.measured}
      data-ideal={item.ideal}
    >
      <div className="w-48 flex-shrink-0 text-right pr-2 flex items-center justify-end gap-2">
        {StatusIcon}
        <p className="text-sm text-slate-600 leading-tight">
          {item.name}
          {notAssessed && <span className="block text-[11px] text-slate-500" data-testid={`sound-not-assessed-${index}`}>Not assessed (turbo unknown)</span>}
        </p>
      </div>

      <div className="flex-1 relative h-9">
        {!measuredExceedsIdeal ? (
          /* measured ≤ ideal — solid bar + gray "ideal" zone */
          <>
            <div data-testid={`sound-measured-bar-${index}`} className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${measuredPct}%`, backgroundColor: solidColor }} />
            <div data-testid={`sound-ideal-zone-${index}`} className="absolute inset-y-0 rounded-sm" style={{ left: `${measuredPct}%`, width: `${idealPct - measuredPct}%`, ...postServiceStyle }} />
          </>
        ) : (
          /* measured > ideal — full bar + darker overlay showing where ideal sits */
          <>
            <div data-testid={`sound-measured-bar-${index}`} className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${measuredPct}%`, backgroundColor: solidColor }} />
            <div data-testid={`sound-ideal-overlay-${index}`} className="absolute inset-y-0 left-0 rounded-sm z-10" style={{ width: `${idealPct}%`, ...idealOverlayStyle }} />
          </>
        )}
      </div>
    </div>
  );
}

export default function MFCWLResults({ result }) {
  const [recommendation, setRecommendation] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [valuation, setValuation] = useState(null);
  const [valLoading, setValLoading] = useState(false);
  const [engineWeightage, setEngineWeightage] = useState(15);

  const rating = result?.engine_rating || {};
  const soundClass = result?.sound_classification || {};
  const soundTypes = soundClass.sound_types || [];
  const soundBarData = prepareSoundBars(soundTypes);
  const overallRating = rating.overall_rating || 0;

  // Fetch recommendation on mount
  useEffect(() => {
    if (result?.id) {
      setRecLoading(true);
      api.getMFCWLRecommendation(result.id)
        .then(r => setRecommendation(r.data?.recommendation))
        .catch(() => {
          const critical = soundBarData.filter(d => d.status === "critical").length;
          const elevated = soundBarData.filter(d => d.status === "elevated").length;
          if (critical > 0) {
            setRecommendation("Critical sound deviations detected. Immediate mechanical inspection recommended before purchase or resale.");
          } else if (elevated > 0) {
            setRecommendation("Some elevated sound readings detected. Recommend servicing within next 5,000 km and monitoring during routine maintenance.");
          } else {
            setRecommendation("Engine is in good health. All sound readings within expected thresholds. Follow standard service schedule.");
          }
        })
        .finally(() => setRecLoading(false));
    }
  }, [result?.id]);

  // Fetch valuation when weightage changes
  const fetchValuation = useCallback((weightage) => {
    if (!result?.id) return;
    setValLoading(true);
    api.getMFCWLValuation(result.id, weightage)
      .then(r => setValuation(r.data))
      .catch(() => {
        const score10 = Math.round((overallRating / 10) * 10) / 10;
        const impact = Math.round(((8 - score10) / 10) * weightage * 10) / 10;
        setValuation({
          valuation_text: `With engine carrying ${weightage}% of total valuation and an AI score of ${score10}/10, the estimated valuation impact is ${impact > 0 ? '-' : ''}${Math.abs(impact)}%.`,
          ai_score: score10, standard_score: 8, engine_weightage: weightage,
          valuation_impact_pct: impact, adjustment: impact > 0 ? "down" : "neutral",
        });
      })
      .finally(() => setValLoading(false));
  }, [result?.id, overallRating]);

  useEffect(() => {
    fetchValuation(engineWeightage);
  }, []);

  if (!result) return null;

  // Engine Health zones — 3 colors only, consistent everywhere
  const getZoneInfo = (score) => {
    if (score >= 70) return { zone: "Good", color: "#22C55E", bg: "bg-emerald-500" };
    if (score >= 40) return { zone: "Acceptable", color: "#F59E0B", bg: "bg-amber-500" };
    return { zone: "Repair Zone", color: "#EF4444", bg: "bg-red-500" };
  };
  const zoneInfo = getZoneInfo(overallRating);
  const ratingOutOf10 = Math.round((overallRating / 10) * 10) / 10;

  // Calculate max scale for bars (use the largest value + 15% padding)
  const allValues = soundBarData.flatMap(d => [d.measured, d.ideal]);
  const maxVal = Math.max(...allValues, 60) * 1.15;

  return (
    <div className="space-y-6" data-testid="mfcwl-results">
      {/* MFCWL Sound Bars */}
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <CardContent className="p-6">
          {/* Legend */}
          <div className="flex items-center justify-end gap-6 mb-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#4CD964' }} /> Current (OK)
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#FFB347' }} /> Current (Elevated)
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#EF4444' }} /> Current (Critical)
            </span>
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-sm inline-block bg-gray-200 border border-slate-300" />
              Ideal Sound Level
            </span>
          </div>

          {/* Sound bars */}
          <div>
            {soundBarData.map((item, idx) => {
              if (item.measured === 0 && item.ideal === 0) return null;
              return <SoundBar key={idx} item={item} maxVal={maxVal} index={idx} />;
            })}
          </div>
        </CardContent>
      </Card>

      {/* Engine Health Bar (horizontal zone bar like the reference) */}
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-3">
            <p className="text-sm font-bold text-slate-700">
              Engine Health — {ratingOutOf10}/10 ({zoneInfo.zone})
            </p>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#EF4444' }} /> Repair Zone</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#F59E0B' }} /> Acceptable</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#22C55E' }} /> Good</span>
            </div>
          </div>

          {/* Zone labels above bar */}
          <div className="flex ml-32">
            <div style={{ width: '40%' }} className="text-center text-xs text-slate-400">Repair Zone</div>
            <div style={{ width: '30%' }} className="text-center text-xs text-slate-400">Acceptable</div>
            <div style={{ width: '30%' }} className="text-center text-xs text-slate-400">Good</div>
          </div>

          {/* Engine Health horizontal bar */}
          <div className="flex items-center gap-3 mt-1">
            <div className="w-28 flex-shrink-0 text-right pr-2">
              <p className="text-sm text-slate-600">Engine Health</p>
            </div>
            <div className="flex-1 relative h-10 rounded-sm overflow-hidden flex border border-slate-200">
              {/* Zone backgrounds - all grey */}
              <div className="h-full bg-gray-200" style={{ width: '40%' }} />
              <div className="h-full bg-gray-200" style={{ width: '30%' }} />
              <div className="h-full bg-gray-200" style={{ width: '30%' }} />
              {/* Current rating bar overlay */}
              <div className="absolute inset-y-0 left-0 rounded-sm flex items-center justify-end pr-2" style={{ width: `${overallRating}%`, backgroundColor: zoneInfo.color }}>
                <span className="text-xs font-bold text-white drop-shadow">{ratingOutOf10}</span>
              </div>
              {/* Zone boundary lines */}
              <div className="absolute inset-y-0 border-r border-dashed border-slate-400" style={{ left: '40%' }} />
              <div className="absolute inset-y-0 border-r border-dashed border-slate-400" style={{ left: '70%' }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Recommendation */}
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex items-center gap-2">
          <Wrench size={18} className="text-slate-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Analysis</span>
        </div>
        <CardContent className="p-6">
          {recLoading ? (
            <div className="flex items-center gap-3 text-slate-400">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Generating recommendation...</span>
            </div>
          ) : recommendation ? (
            <p className="text-sm text-slate-700 leading-relaxed" data-testid="mfcwl-recommendation">{recommendation}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* Valuation Recommendation */}
      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale size={18} className="text-slate-500" />
            <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Valuation Impact Analysis</span>
          </div>
        </div>
        <CardContent className="p-6 space-y-5">
          {/* Engine Weightage Dropdown */}
          <div className="flex items-center gap-4">
            <p className="text-sm font-medium text-slate-600">Engine Weightage in Total Valuation</p>
            <select
              value={engineWeightage}
              onChange={(e) => {
                const w = parseInt(e.target.value);
                setEngineWeightage(w);
                fetchValuation(w);
              }}
              className="h-9 px-3 rounded-sm border border-slate-200 bg-white text-sm font-bold font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="engine-weightage-select"
            >
              {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map(v => (
                <option key={v} value={v}>{v}%</option>
              ))}
            </select>
          </div>

          {/* Impact Summary */}
          {valuation && !valLoading && (
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-slate-50 rounded-sm border border-slate-100 text-center">
                <p className="text-xs font-bold uppercase text-slate-400">AI Engine Score</p>
                <p className="text-xl font-bold font-mono" style={{ color: valuation.ai_score >= 7 ? '#22C55E' : valuation.ai_score >= 5 ? '#F59E0B' : '#EF4444' }}>
                  {valuation.ai_score}/10
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm border border-slate-100 text-center">
                <p className="text-xs font-bold uppercase text-slate-400">Engine Weightage</p>
                <p className="text-xl font-bold font-mono text-slate-700">{valuation.engine_weightage}%</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm border border-slate-100 text-center">
                <p className="text-xs font-bold uppercase text-slate-400">Valuation Impact</p>
                <p className={`text-xl font-bold font-mono ${valuation.valuation_impact_pct > 0 ? 'text-red-500' : valuation.valuation_impact_pct < 0 ? 'text-emerald-500' : 'text-slate-600'}`}>
                  {valuation.valuation_impact_pct > 0 ? '-' : valuation.valuation_impact_pct < 0 ? '+' : ''}{Math.abs(valuation.valuation_impact_pct)}%
                </p>
              </div>
            </div>
          )}

          {/* AI Valuation Text */}
          {valLoading ? (
            <div className="flex items-center gap-3 text-slate-400">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Generating valuation recommendation...</span>
            </div>
          ) : valuation?.valuation_text ? (
            <p className="text-sm text-slate-700 leading-relaxed" data-testid="mfcwl-valuation-text">{valuation.valuation_text}</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
